import { Currency } from '@/modules/common';
import { QuickBooksWarning } from '@/modules/finance/common';
import { ProjectBillingModel, ProjectListItem } from '@/modules/projects';

import { QuickBooksProjectWarningTypeDTO } from './dto/response/quick-books-project-warning.response';

/**
 * Invoice hours source type - domain enum
 */
export enum InvoiceHoursSourceType {
  TRACKED_BILLABLE = 'TRACKED_BILLABLE',
  TRACKED_TOTAL = 'TRACKED_TOTAL',
  BOOKED_BILLABLE = 'BOOKED_BILLABLE',
  BOOKED_TOTAL = 'BOOKED_TOTAL',
  CAPACITY = 'CAPACITY',
  APPROVED_BILLABLE = 'APPROVED_BILLABLE',
  APPROVED_TOTAL = 'APPROVED_TOTAL',
  TIME_OFF = 'TIME_OFF',
}

export enum GenerateInvoiceSubRowType {
  MEMBER = 'MEMBER',
  ADDED_MEMBER = 'ADDED_MEMBER',
  OVERHEAD = 'OVERHEAD',
  ADD_OVERHEAD = 'ADD_OVERHEAD',
}

/**
 * Invoice hours source - domain model
 * Represents hours from a specific source with calculated rate and amount
 */
export interface InvoiceHoursSource {
  sourceType: InvoiceHoursSourceType;
  hours: number;
  amount: number;
  currencyCode: string;
  averageRate: number;
}

type InvoiceBy = InvoiceHoursSourceType;

interface GenerateInvoiceBase {
  id: string;
  invoiceBy: string;
  hours: number;
  effectiveRate: number;
  currency: Currency;
  total: number;
}

export interface GenerateInvoiceProjectTeamMemberListItem extends GenerateInvoiceBase {
  type: GenerateInvoiceSubRowType.MEMBER;
  name: string;
  avatarUrl: string;
  invoiceBy: InvoiceBy;
  hoursSources: InvoiceHoursSource[];
}

export interface GenerateInvoiceProjectOverheadListItem {
  type: GenerateInvoiceSubRowType.OVERHEAD;
  id: string;
  description: string;
  effectiveRate: number;
  currency: Currency;
  /**
   * Id of the PENDING income overhead this row was scheduled from (Overheads
   * page). Undefined for ad-hoc lines added on the spot. Passed through the
   * create-invoice payload so the backend marks the overhead INVOICED.
   */
  overheadId?: number;
  /** Calendar date of the scheduled overhead (ISO, start-of-day UTC). */
  date?: string;
}

export interface GenerateInvoiceAddedMemberListItem {
  type: GenerateInvoiceSubRowType.ADDED_MEMBER;
  id: string;
  memberId: number | null;
  memberName: string;
  avatarUrl: string;
  hours: number;
  effectiveRate: number;
  currency: Currency;
  total: number;
}

export interface GenerateInvoiceAddOverheadButtonRow {
  type: GenerateInvoiceSubRowType.ADD_OVERHEAD;
  projectId: string;
  currencyCode: string;
}

export type GenerateInvoiceSubRow =
  | GenerateInvoiceProjectTeamMemberListItem
  | GenerateInvoiceAddedMemberListItem
  | GenerateInvoiceProjectOverheadListItem
  | GenerateInvoiceAddOverheadButtonRow;

export interface GenerateInvoiceListItem extends GenerateInvoiceBase {
  project: Pick<ProjectListItem, 'id' | 'colorHex' | 'name' | 'logoUrl'> & {
    clientName: string | null;
    billingModel: ProjectBillingModel | null;
    warnings: QuickBooksWarning<QuickBooksProjectWarningTypeDTO>[];
  };
  projectMembers: GenerateInvoiceProjectTeamMemberListItem[];
  projectAddedMembers: GenerateInvoiceAddedMemberListItem[];
  projectOverheads: GenerateInvoiceProjectOverheadListItem[];
  projectSubRows: GenerateInvoiceSubRow[];
  /** Payment-details entry selected for this invoice. Attached by the
   *  page's payload builder; undefined when the feature flag is off, null = "None". */
  paymentDetailsId?: number | null;
}
